// The voice orb: a soft, swirling sphere that is the face of a voice conversation. It drifts
// while listening and swells with your voice, spins up while it's thinking, and pulses with its
// own voice while it speaks. Colours come from one CSS colour (the theme's primary, or an
// agent's colour); the other two tones are derived from it in CSS (see `.voice-orb` in
// index.css), so it fits every theme in light and dark.
import { useEffect, useRef, type CSSProperties } from "react";

import type { VoiceStatus } from "./useVoiceConversation";

export function VoiceOrb({
  size,
  color = "var(--primary)",
  status = "idle",
  levels,
  className = "",
}: {
  size: number;
  /** Base colour (any CSS colour). */
  color?: string;
  status?: VoiceStatus;
  /** Live loudness (0–1) of you and of it; read every frame while the orb is on screen. */
  levels?: () => { input: number; output: number };
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const statusRef = useRef(status);
  statusRef.current = status;

  // Loudness → CSS variables once per frame, smoothed, without re-rendering React.
  useEffect(() => {
    if (!levels) return;
    let frame = 0;
    let smooth = 0;
    const tick = () => {
      const { input, output } = levels();
      const now = statusRef.current;
      const target = now === "speaking" ? output : now === "listening" ? input : 0;
      smooth += (target - smooth) * (target > smooth ? 0.35 : 0.12);
      ref.current?.style.setProperty("--orb-level", smooth.toFixed(3));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [levels]);

  return (
    <span
      ref={ref}
      aria-hidden
      data-status={status}
      className={`voice-orb ${className}`}
      style={{ "--orb-a": color, "--orb-size": `${size}px` } as CSSProperties}
    >
      <span className="voice-orb-halo" />
      <span className="voice-orb-body">
        <span className="voice-orb-swirl" />
        <span className="voice-orb-swirl voice-orb-swirl-2" />
        <span className="voice-orb-gloss" />
      </span>
    </span>
  );
}
